import { useState, useEffect } from "react";
import { getRestaurants, createRestaurant, updateRestaurant, deleteRestaurant, setPreferredRestaurant } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Store, Loader2, MapPin, Phone, Star } from "lucide-react";

export default function RestaurantsPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
  });

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    try {
      const response = await getRestaurants();
      setRestaurants(response.data);
    } catch (error) {
      toast.error("Failed to load restaurants");
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedRestaurant(null);
    setFormData({ name: "", address: "", phone: "" });
    setIsDialogOpen(true);
  };

  const openEditDialog = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setFormData({
      name: restaurant.name || "",
      address: restaurant.address || "",
      phone: restaurant.phone || "",
    });
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setIsDeleteOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Restaurant name is required");
      return;
    }
    
    setIsSaving(true);
    try {
      if (selectedRestaurant) {
        await updateRestaurant(selectedRestaurant.id, formData);
        toast.success("Restaurant updated");
      } else {
        await createRestaurant(formData);
        toast.success("Restaurant created");
      }
      setIsDialogOpen(false);
      loadRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save restaurant");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (restaurant) => {
    try {
      await updateRestaurant(restaurant.id, { is_active: !restaurant.is_active });
      loadRestaurants();
      toast.success(restaurant.is_active ? "Restaurant deactivated" : "Restaurant activated");
    } catch (error) {
      toast.error("Failed to update restaurant");
    }
  };

  const handleSetPreferred = async (restaurant) => {
    if (restaurant.is_preferred) return;
    try {
      await setPreferredRestaurant(restaurant.id);
      loadRestaurants();
      toast.success(`"${restaurant.name}" set as preferred — Combined Orders will use its name`);
    } catch (error) {
      toast.error("Failed to set preferred restaurant");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRestaurant(selectedRestaurant.id);
      toast.success("Restaurant deleted");
      setIsDeleteOpen(false);
      loadRestaurants();
    } catch (error) {
      toast.error("Failed to delete restaurant");
    }
  };

  return (
    <AppLayout title="Restaurants" showBack>
      <div className="p-4 md:p-6 pb-24 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : restaurants.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 mb-4">No restaurants yet</p>
            <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-first-restaurant">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Restaurant
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {restaurants.map((restaurant, index) => (
              <Card 
                key={restaurant.id} 
                className={`card-hover animate-in stagger-${(index % 4) + 1} ${!restaurant.is_active ? 'opacity-60' : ''}`}
                data-testid={`restaurant-card-${restaurant.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-kale-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Store className="w-5 h-5 text-kale-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-stone-900 truncate">{restaurant.name}</h3>
                        {restaurant.is_preferred && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full shrink-0" data-testid={`preferred-badge-${restaurant.id}`}>
                            <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Preferred
                          </span>
                        )}
                      </div>
                      <div className="mt-1 space-y-1 text-sm text-stone-500">
                        {restaurant.address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">{restaurant.address}</span>
                          </div>
                        )}
                        {restaurant.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{restaurant.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSetPreferred(restaurant)}
                        className={`h-9 w-9 ${restaurant.is_preferred ? 'text-amber-500' : 'text-stone-400 hover:text-amber-500'}`}
                        title={restaurant.is_preferred ? "Preferred restaurant" : "Set as preferred"}
                        data-testid={`preferred-restaurant-${restaurant.id}`}
                      >
                        <Star className={`w-4 h-4 ${restaurant.is_preferred ? 'fill-amber-500' : ''}`} />
                      </Button>
                      <Switch
                        checked={restaurant.is_active !== false}
                        onCheckedChange={() => handleToggleActive(restaurant)}
                        data-testid={`toggle-restaurant-${restaurant.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(restaurant)}
                        className="h-9 w-9 text-stone-500 hover:text-kale-700"
                        data-testid={`edit-restaurant-${restaurant.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(restaurant)}
                        className="h-9 w-9 text-stone-500 hover:text-red-600"
                        data-testid={`delete-restaurant-${restaurant.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Add Button */}
      <div className="sticky-bottom">
        <Button onClick={openCreateDialog} className="w-full btn-primary" data-testid="add-restaurant-button">
          <Plus className="w-4 h-4 mr-2" /> Add Restaurant
        </Button>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedRestaurant ? "Edit Restaurant" : "Add Restaurant"}</DialogTitle>
            <DialogDescription>
              {selectedRestaurant ? "Update restaurant information below." : "Fill in the restaurant details below."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Restaurant Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Restaurant 1 - Downtown"
                className="input-mobile"
                data-testid="restaurant-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g., 123 Main St, City"
                className="input-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., 555-0123"
                className="input-mobile"
              />
            </div>
            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 h-11">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={isSaving} data-testid="save-restaurant-button">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Restaurant?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedRestaurant?.name}"? This will also delete all associated vendors, items, and orders. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
